import * as THREE from "three";

const MAG_SIZE = 12;
const FIRE_COOLDOWN = 0.24;
const RELOAD_TIME = 1.4;
const SEED_FLIGHT_TIME = 0.07;
const MAX_SEED_VISUALS = 24;

class WeaponSystem {
  constructor({
    camera,
    collidables,
    flowerSpawner,
    bots,
    onAmmoChange,
    onShot,
    onBotImpact,
    onEmptyClick,
  }) {
    this.camera = camera;
    this.collidables = collidables;
    this.flowerSpawner = flowerSpawner;
    this.bots = bots;
    this.onAmmoChange = onAmmoChange;
    this.onShot = onShot;
    this.onBotImpact = onBotImpact;
    this.onEmptyClick = onEmptyClick;

    this.ammo = MAG_SIZE;
    this.reloading = false;
    this.reloadTimer = 0;
    this.cooldown = 0;
    this.firing = false;

    this.raycaster = new THREE.Raycaster();
    this.center = new THREE.Vector2(0, 0);
    this.muzzleWorld = new THREE.Vector3();

    this.viewmodel = new THREE.Group();
    this.podMat = new THREE.MeshStandardMaterial({
      color: 0x3a2d24,
      roughness: 0.8,
    });
    this.ringMat = new THREE.MeshStandardMaterial({
      color: 0x8aa06a,
      roughness: 0.7,
    });
    const pod = new THREE.Mesh(
      new THREE.SphereGeometry(0.095, 14, 12),
      this.podMat
    );
    pod.scale.set(1, 1.15, 1.45);
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.032, 0.05, 0.34, 10),
      this.podMat
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.02, -0.22);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.075, 0.02, 8, 16),
      this.ringMat
    );
    ring.position.set(0, 0.02, -0.1);
    this.viewmodel.add(pod, barrel, ring);
    this.viewmodel.position.set(0.3, -0.27, -0.55);
    this.camera.add(this.viewmodel);

    this.seedGeo = new THREE.IcosahedronGeometry(0.05, 0);
    this.seedMat = new THREE.MeshStandardMaterial({
      color: 0xd9e8b8,
      emissive: 0x9fb86a,
      emissiveIntensity: 0.6,
      roughness: 0.5,
    });
    this.seedPool = [];
    this.activeSeeds = [];

    this.recoilKick = 0;
    this.swayX = 0;
    this.swayY = 0;
    this.lastYaw = 0;
    this.lastPitch = 0;
    this.emitAmmoState();
  }

  emitAmmoState() {
    if (this.onAmmoChange) this.onAmmoChange(this.ammo, this.reloading);
  }

  setFiring(value) {
    this.firing = value;
    if (!value && this.ammo <= 0 && !this.reloading) this.startReload();
  }

  startReload() {
    if (this.reloading || this.ammo === MAG_SIZE) return;
    this.reloading = true;
    this.reloadTimer = RELOAD_TIME;
    this.emitAmmoState();
  }

  requestReload() {
    this.startReload();
  }

  shoot() {
    this.ammo -= 1;
    this.cooldown = FIRE_COOLDOWN;
    this.recoilKick = 1;
    if (this.onShot) this.onShot();
    this.emitAmmoState();

    this.raycaster.setFromCamera(this.center, this.camera);
    const targets = [...this.collidables];
    if (this.bots) targets.push(...this.bots.getHitTargets());
    const hits = this.raycaster.intersectObjects(targets, true);
    const hit = hits.find((h) => h.distance > 0.15);

    let endPoint;
    if (hit) {
      endPoint = hit.point.clone();
      const botId = hit.object.userData.botId;
      if (botId != null && hit.object.userData.botAlive !== false) {
        this.bots.applyDamage(botId, hit.point);
        if (this.onBotImpact) this.onBotImpact(hit.point);
      } else {
        const normal = hit.face
          ? hit.face.normal
              .clone()
              .transformDirection(hit.object.matrixWorld)
              .normalize()
          : hit.point.clone().sub(this.camera.getWorldPosition(new THREE.Vector3())).normalize().negate();
        this.flowerSpawner.spawnFlower(hit.point, normal);
      }
    } else {
      endPoint = this.raycaster.ray.at(60, new THREE.Vector3());
    }
    this.spawnSeedVisual(endPoint);
  }

  spawnSeedVisual(endPoint) {
    let mesh = this.seedPool.pop();
    if (!mesh) {
      mesh = new THREE.Mesh(this.seedGeo, this.seedMat);
    }
    mesh.visible = true;
    const from = this.muzzleWorld.set(0.28, -0.22, -0.75).clone();
    this.camera.localToWorld(from);
    mesh.position.copy(from);
    this.camera.parent.add(mesh);
    this.activeSeeds.push({
      mesh,
      from,
      to: endPoint,
      t: 0,
    });
    if (this.activeSeeds.length > MAX_SEED_VISUALS) {
      const oldest = this.activeSeeds.shift();
      this.retireSeed(oldest.mesh);
    }
  }

  retireSeed(mesh) {
    mesh.visible = false;
    if (mesh.parent) mesh.parent.remove(mesh);
    if (this.seedPool.length < MAX_SEED_VISUALS) this.seedPool.push(mesh);
  }

  update(dt, elapsed, playerYaw, playerPitch) {
    this.cooldown -= dt;

    if (this.reloading) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        this.reloading = false;
        this.ammo = MAG_SIZE;
        this.emitAmmoState();
      }
    }

    if (this.firing && !this.reloading && this.cooldown <= 0) {
      if (this.ammo > 0) {
        this.shoot();
      } else {
        this.startReload();
        if (this.onEmptyClick) this.onEmptyClick();
      }
    }

    for (let i = this.activeSeeds.length - 1; i >= 0; i -= 1) {
      const seed = this.activeSeeds[i];
      seed.t += dt / SEED_FLIGHT_TIME;
      if (seed.t >= 1) {
        this.retireSeed(seed.mesh);
        this.activeSeeds.splice(i, 1);
      } else {
        seed.mesh.position.lerpVectors(seed.from, seed.to, seed.t);
        const s = 1 - seed.t * 0.6;
        seed.mesh.scale.setScalar(s);
      }
    }

    const yawDelta = playerYaw - this.lastYaw;
    const pitchDelta = playerPitch - this.lastPitch;
    this.lastYaw = playerYaw;
    this.lastPitch = playerPitch;
    this.swayX += (-yawDelta * 2.4 - this.swayX) * Math.min(1, dt * 10);
    this.swayY += (pitchDelta * 2 - this.swayY) * Math.min(1, dt * 10);
    this.recoilKick = Math.max(0, this.recoilKick - dt * 6);

    const bobT = Math.sin(elapsed * 9) * 0.004;
    this.viewmodel.position.set(
      0.3 + THREE.MathUtils.clamp(this.swayX, -0.05, 0.05),
      -0.27 + bobT + THREE.MathUtils.clamp(this.swayY, -0.04, 0.04),
      -0.55 + this.recoilKick * 0.09
    );
    this.viewmodel.rotation.x = this.recoilKick * 0.18 + this.swayY * 0.5;
    this.viewmodel.rotation.y = this.swayX * 0.6;
  }

  dispose() {
    if (this.viewmodel.parent) this.viewmodel.parent.remove(this.viewmodel);
    this.viewmodel.traverse((child) => {
      if (child.isMesh) child.geometry.dispose();
    });
    this.podMat.dispose();
    this.ringMat.dispose();
    this.seedGeo.dispose();
    this.seedMat.dispose();
    this.activeSeeds.forEach((seed) => {
      if (seed.mesh.parent) seed.mesh.parent.remove(seed.mesh);
    });
    this.seedPool.forEach((mesh) => {
      if (mesh.parent) mesh.parent.remove(mesh);
    });
    this.activeSeeds = [];
    this.seedPool = [];
  }
}

export { WeaponSystem, MAG_SIZE };
export default WeaponSystem;
